import { useState, useEffect, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { monitoringService, RecordsPivotResponse } from '@/services/monitoring'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Loader2, XCircle, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, Clock, Calendar, Zap, Filter, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

// Behavior category display config (simplified system)
const BEHAVIOR_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  hourly: {
    label: 'Hourly',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    icon: <Clock className="h-3 w-3" />
  },
  daily: {
    label: 'Daily',
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    icon: <Calendar className="h-3 w-3" />
  },
  async: {
    label: 'Async',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    icon: <Zap className="h-3 w-3" />
  }
}

// Async segments (email, bucket, push)
const ASYNC_SEGMENTS = ['email', 'bucket', 'push']

// Available behavior categories for filtering
const BEHAVIOR_CATEGORIES = ['hourly', 'daily', 'async'] as const
type BehaviorCategory = typeof BEHAVIOR_CATEGORIES[number]

interface RecsPivotProps {
  organizationId?: number
}

export function RecsPivot({ organizationId }: RecsPivotProps) {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<RecordsPivotResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sortColumn, setSortColumn] = useState<string | null>(null) // date string or null
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [behaviorFilter, setBehaviorFilter] = useState<Set<BehaviorCategory>>(new Set(BEHAVIOR_CATEGORIES))
  const [searchTerm, setSearchTerm] = useState('')

  // Calculate date range (last 7 days: d-1 to d-7)
  const getDateRange = () => {
    const endDate = new Date()
    endDate.setDate(endDate.getDate() - 1)  // Yesterday (d-1)
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 7)  // 7 days ago (d-7)

    const formatDate = (d: Date) => d.toISOString().split('T')[0]
    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const { startDate, endDate } = getDateRange()
      const response = await monitoringService.getRecordsPivot(startDate, endDate, organizationId)
      setData(response)
    } catch (err: any) {
      console.error('Failed to fetch records pivot:', err)
      setError(err.response?.data?.detail || 'Failed to load records pivot data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [organizationId])

  // Format date for column header (e.g., "Jan 13")
  const formatDateHeader = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00Z')
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  }

  // Format number with comma separators
  const formatNumber = (num: number): string => {
    return num.toLocaleString()
  }

  // Handle column sort click
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to desc (highest first) for dates, asc for ID
      setSortColumn(column)
      setSortDirection(column === 'id' ? 'asc' : 'desc')
    }
  }

  // Filter and sort integrations
  const filteredAndSortedIntegrations = useMemo(() => {
    if (!data?.integrations) return []

    let filtered = data.integrations

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim()
      filtered = filtered.filter(integration =>
        integration.integration_name.toLowerCase().includes(term) ||
        String(integration.integration_id).includes(term)
      )
    }

    // Apply behavior filter
    if (behaviorFilter.size < BEHAVIOR_CATEGORIES.length) {
      filtered = filtered.filter(integration => {
        // Async integrations are determined by segment (email, bucket, push)
        const isAsync = ASYNC_SEGMENTS.includes(integration.segment || '')
        const category: BehaviorCategory = isAsync ? 'async' : (integration.behavior_category === 'daily' ? 'daily' : 'hourly')
        return behaviorFilter.has(category)
      })
    }

    // Apply sorting
    if (!sortColumn) return filtered

    return [...filtered].sort((a, b) => {
      if (sortColumn === 'id') {
        return sortDirection === 'asc'
          ? a.integration_id - b.integration_id
          : b.integration_id - a.integration_id
      }
      const aVal = a.daily_records[sortColumn] ?? 0
      const bVal = b.daily_records[sortColumn] ?? 0
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
    })
  }, [data?.integrations, sortColumn, sortDirection, behaviorFilter, searchTerm])

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-lg font-semibold">Failed to load records pivot</p>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </div>
      </div>
    )
  }

  if (!data) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Records Collected
              <Badge variant="secondary">
                {behaviorFilter.size < BEHAVIOR_CATEGORIES.length
                  ? `${filteredAndSortedIntegrations.length}/${data.total_integrations}`
                  : data.total_integrations}
              </Badge>
            </CardTitle>
            <CardDescription>
              Daily records collected from clients per integration (last 7 days)
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Search Row */}
        <div className="flex flex-wrap gap-4 mb-4 items-center">
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by integration ID or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-md border overflow-x-auto [--webkit-overflow-scrolling:touch]" style={{ WebkitOverflowScrolling: 'touch' }}>
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow>
                <TableHead
                  className="sticky left-0 bg-background z-10 min-w-[80px] cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center gap-1">
                    ID
                    {sortColumn === 'id' ? (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      )
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-30" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="sticky left-[80px] bg-background z-10 min-w-[200px]">Integration</TableHead>
                <TableHead className="min-w-[80px]">
                  <div className="flex items-center gap-1">
                    Behavior
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-6 px-1 ${behaviorFilter.size < BEHAVIOR_CATEGORIES.length ? 'bg-primary/10 text-primary' : ''}`}
                        >
                          <Filter className="h-3 w-3" />
                          {behaviorFilter.size < BEHAVIOR_CATEGORIES.length && (
                            <span className="ml-1 text-xs">{behaviorFilter.size}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-44 p-2" align="start">
                        <div className="space-y-2">
                          {BEHAVIOR_CATEGORIES.map((category) => {
                            const config = BEHAVIOR_CONFIG[category]
                            return (
                              <div key={category} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`behavior-${category}`}
                                  checked={behaviorFilter.has(category)}
                                  onCheckedChange={(checked) => {
                                    const newFilter = new Set(behaviorFilter)
                                    if (checked) {
                                      newFilter.add(category)
                                    } else {
                                      newFilter.delete(category)
                                    }
                                    // Ensure at least one is selected
                                    if (newFilter.size > 0) {
                                      setBehaviorFilter(newFilter)
                                    }
                                  }}
                                />
                                <Label
                                  htmlFor={`behavior-${category}`}
                                  className="text-sm cursor-pointer flex items-center gap-1"
                                >
                                  {config.icon}
                                  {config.label}
                                </Label>
                              </div>
                            )
                          })}
                          <div className="border-t pt-2 mt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full h-7 text-xs"
                              onClick={() => setBehaviorFilter(new Set(BEHAVIOR_CATEGORIES))}
                            >
                              Select All
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </TableHead>
                {data.dates.map((date) => (
                  <TableHead
                    key={date}
                    className="text-right min-w-[90px] cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort(date)}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {formatDateHeader(date)}
                      {sortColumn === date ? (
                        sortDirection === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedIntegrations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3 + data.dates.length} className="text-center py-12">
                    <p className="text-muted-foreground">No records found for the last 7 days</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedIntegrations.map((integration) => {
                  // Async integrations are determined by segment (email, bucket, push)
                  const isAsync = ASYNC_SEGMENTS.includes(integration.segment || '')
                  const category = isAsync ? 'async' : (integration.behavior_category === 'daily' ? 'daily' : 'hourly')
                  const behaviorConfig = BEHAVIOR_CONFIG[category]
                  return (
                  <TableRow key={integration.integration_id}>
                    <TableCell className="sticky left-0 bg-background font-mono text-xs">
                      <Link
                        to={`/${orgSlug}/integrations/${integration.integration_id}/edit`}
                        className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        {integration.integration_id}
                      </Link>
                    </TableCell>
                    <TableCell className="sticky left-[80px] bg-background font-medium text-sm">
                      {integration.integration_name}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${behaviorConfig.color} gap-1 text-xs`}>
                        {behaviorConfig.icon}
                        {behaviorConfig.label}
                      </Badge>
                    </TableCell>
                    {data.dates.map((date) => {
                      const records = integration.daily_records[date] ?? 0
                      const isZero = records === 0
                      return (
                        <TableCell
                          key={date}
                          className={`text-right font-mono text-xs ${
                            isZero ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30' : ''
                          }`}
                        >
                          {formatNumber(records)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
